import { ReactNode, useEffect } from 'react';
import WarningIcon from 'components/cico/components/icons/warning.svg';
import { SnackbarAction } from 'notistack';
import styled, { css } from 'styled-components';
import { FlexAlignCenter } from 'components/primitives/flex';
import useRudderStackTrack from 'hooks/useRudderStackTrack';

export type BannerProps = {
  children: ReactNode;
  variant: 'success' | 'error' | 'warning';
  onDismiss: () => void;
  onClick: () => void;
  action?: SnackbarAction;
};

const Banner = ({ action, variant, children, onDismiss, onClick }: BannerProps) => {
  const rudderTrack = useRudderStackTrack();

  useEffect(() => {
    if (!variant) return;

    rudderTrack('banner_user_message', {
      variant,
      text: children,
      type: variant,
    });
  }, [rudderTrack, variant, children]);

  return (
    <BannerContainer onClick={onClick ?? onDismiss} variant={variant} data-testid="banner">
      <FlexAlignCenter>
        {variant === 'success' && <CheckCircleIcon />}
        {variant === 'error' && <WarningIcon />}
      </FlexAlignCenter>
      <div>
        {children}
        {action as ReactNode}
      </div>
      {onDismiss && (
        <FlexAlignCenter ml="auto">
          <StyledCloseIconButton onClick={onDismiss} />
        </FlexAlignCenter>
      )}
    </BannerContainer>
  );
};

const BannerContainer = styled(FlexAlignCenter).attrs({ gap: '10px' })<{ variant: BannerProps['variant'] }>`
  padding: 18px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  width: 100%;
  cursor: pointer;

  ${({ variant }) =>
    variant === 'success' &&
    css`
      color: #000;
      background-color: #72d4ba;
    `}
  ${({ variant }) =>
    variant === 'error' &&
    css`
      color: #fff;
      background-color: #bc350a;
    `}
`;

const CloseIconButton = (props) => (
  <svg width={24} height={24} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.718 18.718a9.5 9.5 0 1 1 0-13.435 9.5 9.5 0 0 1 0 13.435Zm-1.061-1.061A8 8 0 1 1 6.343 6.343a8 8 0 0 1 11.314 11.314Zm-8.93-8.93a.75.75 0 0 1 1.06 0L12 10.939l2.041-2.04a.75.75 0 0 1 1.06 1.06L13.062 12l2.212 2.212a.75.75 0 1 1-1.06 1.061L12 13.061l-2.384 2.383a.75.75 0 1 1-1.06-1.06L10.939 12 8.727 9.788a.75.75 0 0 1 0-1.061Z"
      fill="currentColor"
    />
  </svg>
);

const StyledCloseIconButton = styled(CloseIconButton)`
  cursor: pointer;
`;

const CheckCircleIcon = (props) => (
  <svg width={24} height={24} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M12 2.5c-5.242 0-9.5 4.268-9.5 9.5 0 5.242 4.268 9.5 9.5 9.5 5.242 0 9.5-4.268 9.5-9.5.01-5.242-4.258-9.5-9.5-9.5Zm0 17.568c-4.45 0-8.068-3.619-8.068-8.068 0-4.45 3.619-8.068 8.068-8.068 4.45 0 8.068 3.619 8.068 8.068.01 4.45-3.619 8.068-8.068 8.068Zm4.392-11.114a.72.72 0 0 1-.01 1.012l-5.222 5.09a.717.717 0 0 1-.497.2.74.74 0 0 1-.506-.2l-2.52-2.483a.712.712 0 0 1-.01-1.012.72.72 0 0 1 1.012-.01l2.024 1.986 4.726-4.602a.71.71 0 0 1 1.003.02Z"
      fill="#17233C"
    />
  </svg>
);

export default Banner;
